import { syliusFetch, TokenResource } from '../../../entities/@api/Sylius';
import { CheckoutCreateAccountFormData } from '../../../entities/@forms/Checkout/Checkout';
import { RegistrationFormData } from '../../../entities/@forms/RegistrationForm/RegistrationForm';
import { Address, AddressType, CustomerAddress } from '../../../entities/Address/Address';
import { defaultCountry } from '../../../entities/Country/Country';
import { isFetchResultSuccessful } from '../../../entities/FetchResult/FetchResult';
import trans from '../../../helpers/trans';
import { connectOrderToCustomer } from '../../@shop/order/orderActions';
import { ReduxFetchAction } from '../../defaults';
import { addNegativeToast, addPositiveToast } from '../../toast/toastActions';
import { authenticateUser } from '../authentication/authenticationActions';
import { addAddress, fetchCustomer } from '../customer/customerActions';
import { setError, setIsLoading, setIsRegistered } from './registrationSlice';

export const registerUser: ReduxFetchAction<RegistrationFormData> = formData => async dispatch => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));
    dispatch(setIsRegistered(false));

    try {
        const registrationResponse = await syliusFetch<TokenResource>('/shop/customers', {
            method: 'POST',
            body: JSON.stringify(formData),
        });

        if (!isFetchResultSuccessful(registrationResponse)) {
            dispatch(setError(registrationResponse.error));
            return;
        }

        await dispatch(authenticateUser({
            email: formData.email,
            password: formData.password,
            rememberMe: true,
        }));

        await dispatch(fetchCustomer());

        const address: CustomerAddress = {
            id: '',
            type: AddressType.shipping,
            label: trans(`entities.address.type.${AddressType.shipping}`),
            street: formData.street,
            postalCode: formData.postalCode,
            houseNumber: formData.number,
            addition: formData.addition,
            city: formData.city,
            country: formData.countryCode,
        };

        await dispatch(addAddress(address));

        dispatch(addPositiveToast({
            title: trans('redux.registration.successMessageTitle'),
            description: trans('redux.registration.successMessageDescription'),
        }));

        dispatch(setIsRegistered(true));
    } catch (error) {
        console.error('[registerUser]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const registerUserDuringCheckout: ReduxFetchAction<CheckoutCreateAccountFormData> = formData => async dispatch => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));
    dispatch(setIsRegistered(false));

    try {
        const registrationBody = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            password: formData.password,
            subscribedToNewsletter: formData.subscribeToNewsletter,
        };

        const registrationResponse = await syliusFetch<TokenResource>('/shop/customers', {
            method: 'POST',
            body: JSON.stringify(registrationBody),
        });

        if (!isFetchResultSuccessful(registrationResponse)) {
            dispatch(setError(registrationResponse.error));

            dispatch(addNegativeToast({
                title: trans('errors.unknownError'),
                description: registrationResponse.error,
            }));

            return;
        }

        await dispatch(authenticateUser({
            email: formData.email,
            password: formData.password,
            rememberMe: true,
        }));

        await dispatch(fetchCustomer());
        await dispatch(connectOrderToCustomer());

        const shippingAddress: Address = {
            id: '',
            label: trans(`entities.address.type.${AddressType.shipping}`),
            street: formData.shippingStreet,
            houseNumber: formData.shippingHouseNumber,
            addition: formData.shippingAddition,
            postalCode: formData.shippingPostalCode.toUpperCase(),
            city: formData.shippingCity,
            country: defaultCountry.toUpperCase(),
            phoneNumber: formData.phoneNumber,
        };

        const billingAddress: Address = {
            id: '',
            label: trans(`entities.address.type.${AddressType.shipping}`),
            street: formData.billingStreet,
            houseNumber: formData.billingHouseNumber,
            addition: formData.billingAddition,
            postalCode: formData.billingPostalCode.toUpperCase(),
            city: formData.billingCity,
            country: defaultCountry.toUpperCase(),
            phoneNumber: formData.phoneNumber,
        };

        await dispatch(addAddress(shippingAddress));
        await dispatch(addAddress(billingAddress));

        dispatch(addPositiveToast({
            title: trans('redux.registration.successMessageTitle'),
            description: trans('redux.registration.successMessageDescription'),
        }));

        dispatch(setIsRegistered(true));
    } catch (error) {
        console.error('[registerUserDuringCheckout]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};
